// AdminPhotoHistory.jsx - Rezervasyon giriş/çıkış fotoğrafları geçmişi
import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import { collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';

const AdminPhotoHistory = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filter, setFilter] = useState('all'); // all, missing-checkout, today

  const PAGE_SIZE = 20;

  const loadReservations = async (isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setError(null);
      }

      let q;
      const reservationsRef = collection(db, 'reservations');
      
      // Basit sorgu - tüm rezervasyonları getir ve client-side filtrele
      if (filter === 'today') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        q = query(
          reservationsRef,
          where('date', '>=', today),
          where('date', '<', tomorrow),
          limit(PAGE_SIZE * 2)
        );
      } else {
        // Son 100 rezervasyonu getir, client-side filtrele
        q = query(
          reservationsRef,
          orderBy('createdAt', 'desc'),
          limit(100)
        );
      }

      if (isLoadMore && lastDoc) {
        q = query(
          reservationsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(100)
        );
      }

      const snapshot = await getDocs(q);
      let newReservations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side filtreleme
      if (filter === 'all') {
        // Sadece fotoğrafı olanlar
        newReservations = newReservations.filter(r => r.checkInPhoto || r.checkOutPhoto);
      } else if (filter === 'missing-checkout') {
        // Giriş var ama çıkış yok
        newReservations = newReservations.filter(r => r.checkInPhoto && !r.checkOutPhoto);
      } else if (filter === 'today') {
        // Bugünkü tüm rezervasyonlar (fotoğraf olsun olmasın)
        newReservations = newReservations;
      }

      // Tarihe göre sırala (en yeni en üstte)
      newReservations.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      if (isLoadMore) {
        setReservations(prev => [...prev, ...newReservations]);
      } else {
        setReservations(newReservations);
      }

      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length >= 100);

    } catch (err) {
      console.error('Fotoğraf geçmişi yükleme hatası:', err);
      setError('Fotoğraflar yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLastDoc(null);
    loadReservations();
  }, [filter]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    let date;
    if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    return date?.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }) || '-';
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return '-';
    let date;
    if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    return date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) || '-';
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-7 h-7 mr-3 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Fotoğraf Geçmişi
        </h2>

        {/* Filtre */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Tümü
          </button>
          <button
            onClick={() => setFilter('missing-checkout')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'missing-checkout' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Çıkış Eksik
          </button>
          <button
            onClick={() => setFilter('today')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'today' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Bugün
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Fotoğraflar yükleniyor...</p>
        </div>
      ) : reservations.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-gray-400">Henüz fotoğraf yüklenmemiş</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Tarih</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Saat</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Kullanıcı</th>
                  <th className="text-center py-3 px-4 text-gray-400 font-medium">Giriş</th>
                  <th className="text-center py-3 px-4 text-gray-400 font-medium">Çıkış</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((res) => (
                  <tr key={res.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="py-3 px-4 text-white">
                      {formatDate(res.date)}
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {res.startTime} - {res.endTime}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">{res.userName || 'Bilinmiyor'}</p>
                        <p className="text-gray-400 text-sm">{res.userEmail}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {res.checkInPhoto ? (
                        <button
                          onClick={() => setSelectedPhoto({ url: res.checkInPhoto, type: 'Giriş', user: res.userName, date: formatDate(res.date), time: formatTime(res.checkInPhotoAt) })}
                          className="inline-block"
                        >
                          <img 
                            src={res.checkInPhoto} 
                            alt="Giriş" 
                            className="w-16 h-12 object-cover rounded border-2 border-green-500 hover:scale-110 transition-transform cursor-pointer"
                          />
                        </button>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {res.checkOutPhoto ? (
                        <button
                          onClick={() => setSelectedPhoto({ url: res.checkOutPhoto, type: 'Çıkış', user: res.userName, date: formatDate(res.date), time: formatTime(res.checkOutPhotoAt) })}
                          className="inline-block"
                        >
                          <img 
                            src={res.checkOutPhoto} 
                            alt="Çıkış" 
                            className="w-16 h-12 object-cover rounded border-2 border-red-500 hover:scale-110 transition-transform cursor-pointer"
                          />
                        </button>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-900/50 text-yellow-300 text-xs">
                          Eksik
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={() => loadReservations(true)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Daha Fazla Yükle
              </button>
            </div>
          )}
        </>
      )}

      {/* Fotoğraf Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div 
            className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {selectedPhoto.type} Fotoğrafı
                </h3>
                <p className="text-sm text-gray-400">
                  {selectedPhoto.user} • {selectedPhoto.date} • {selectedPhoto.time}
                </p>
              </div>
              <button
                onClick={() => setSelectedPhoto(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <img 
                src={selectedPhoto.url} 
                alt={selectedPhoto.type}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminPhotoHistory;
